'use client';

import Image from 'next/image';
import { useMemo, useState } from 'react';
import {
  ChevronDown,
  Info,
  WalletCards,
  ReceiptText,
  TrendingDown,
  Check,
  X,
  BarChart3,
} from 'lucide-react';

type Country = 'nigeria' | 'ghana' | null;
type Period = 'monthly' | 'annual';
type Residency = 'resident' | 'nonresident';

/* =====================================================
   TAX BAND TABLES
   ===================================================== */

// Nigeria PAYE bands (NTA 2025/2026) — applied to ANNUAL chargeable income
const NIGERIA_TAX_BANDS = [
  { upTo: 800_000, rate: 0 },
  { upTo: 3_000_000, rate: 0.15 },
  { upTo: 12_000_000, rate: 0.18 },
  { upTo: 25_000_000, rate: 0.21 },
  { upTo: 50_000_000, rate: 0.23 },
  { upTo: Infinity, rate: 0.25 },
];

// Ghana PAYE bands (GRA, 2025/2026) — applied to ANNUAL chargeable income
const GHANA_TAX_BANDS = [
  { upTo: 5_880, rate: 0 },
  { upTo: 7_200, rate: 0.05 },
  { upTo: 8_760, rate: 0.1 },
  { upTo: 46_760, rate: 0.175 },
  { upTo: 238_760, rate: 0.25 },
  { upTo: 605_000, rate: 0.3 },
  { upTo: Infinity, rate: 0.35 },
];

const GHANA_SSNIT_RATE = 0.055; // employee tier 1 contribution
const GHANA_SSNIT_MONTHLY_CEILING = 69_000; // GHS insurable earnings ceiling (per month)
const NIGERIA_RENT_RELIEF_RATE = 0.2;
const NIGERIA_RENT_RELIEF_CAP = 500_000; // annual cap, NTA 2025/2026

/**
 * Walks a set of progressive bands and returns tax due on a given annual income.
 * Bands must be sorted ascending by `upTo`, with the final band's upTo = Infinity.
 */
function calculateProgressiveTax(
  annualIncome: number,
  bands: { upTo: number; rate: number }[]
) {
  if (!Number.isFinite(annualIncome) || annualIncome <= 0) return 0;

  let tax = 0;
  let lowerBound = 0;

  for (const band of bands) {
    if (annualIncome <= lowerBound) break;
    const taxableInBand = Math.min(annualIncome, band.upTo) - lowerBound;
    tax += taxableInBand * band.rate;
    lowerBound = band.upTo;
  }

  return tax;
}

/* =====================================================
   GHANA CALCULATION
   ===================================================== */

interface GhanaInputs {
  basicSalary: number;
  cashAllowance: number;
  period: Period;
  residency: Residency;
}

function calculateGhanaTax({
  basicSalary,
  cashAllowance,
  period,
  residency,
}: GhanaInputs) {
  const multiplier = period === 'monthly' ? 12 : 1;

  const basicAnnual = basicSalary * multiplier;
  const cashAllowanceAnnual = cashAllowance * multiplier;
  const grossAnnual = basicAnnual + cashAllowanceAnnual;

  // SSNIT is calculated on BASIC SALARY only, capped at the insurable earnings ceiling.
  // Non-residents are generally exempt (pending consultant confirmation per policy doc).
  const ssnitCeilingAnnual = GHANA_SSNIT_MONTHLY_CEILING * 12;
  const ssnitBaseAnnual = Math.min(basicAnnual, ssnitCeilingAnnual);
  const ssnitAnnual =
    residency === 'resident' ? ssnitBaseAnnual * GHANA_SSNIT_RATE : 0;

  let payeAnnual: number;

  if (residency === 'nonresident') {
    // Flat 25% on Ghana-sourced income, no band calculation, no SSNIT deduction.
    payeAnnual = grossAnnual * 0.25;
  } else {
    const chargeableIncomeAnnual = Math.max(0, grossAnnual - ssnitAnnual);
    payeAnnual = calculateProgressiveTax(chargeableIncomeAnnual, GHANA_TAX_BANDS);
  }

  const netPayAnnual = grossAnnual - ssnitAnnual - payeAnnual;

  const divisor = period === 'monthly' ? 12 : 1;

  return {
    grossPay: grossAnnual / divisor,
    ssnit: ssnitAnnual / divisor,
    paye: payeAnnual / divisor,
    netPay: netPayAnnual / divisor,
  };
}

/* =====================================================
   NIGERIA CALCULATION
   ===================================================== */

interface NigeriaInputs {
  grossIncome: number;
  lifeInsurance: number;
  nhis: number;
  rent: number;
  loanInterest: number;
  pensionContribution: number;
  period: Period;
}

function calculateNigeriaTax({
  grossIncome,
  lifeInsurance,
  nhis,
  rent,
  loanInterest,
  pensionContribution,
  period,
}: NigeriaInputs) {
  const multiplier = period === 'monthly' ? 12 : 1;

  const grossAnnual = grossIncome * multiplier;
  const lifeInsuranceAnnual = lifeInsurance * multiplier;
  const nhisAnnual = nhis * multiplier;
  const rentAnnual = rent;
  const loanInterestAnnual = loanInterest; 
  const pensionAnnual = pensionContribution * multiplier;

  // Rent Relief and Housing Loan Interest Relief are mutually exclusive.
  // If rent is provided, use rent relief; otherwise fall back to loan interest relief.
  let housingRelief = 0;
  if (rentAnnual > 0) {
    housingRelief = Math.min(rentAnnual * NIGERIA_RENT_RELIEF_RATE, NIGERIA_RENT_RELIEF_CAP);
  } else if (loanInterestAnnual > 0) {
    housingRelief = loanInterestAnnual;
  }

  // NHIS and Life Insurance reduce Chargeable Income only — they are self-paid/declared,
  // not payroll deductions, so they do NOT reduce Net Pay.
  const chargeableIncomeAnnual = Math.max(
    0,
    grossAnnual - pensionAnnual - nhisAnnual - lifeInsuranceAnnual - housingRelief
  );

  const payeAnnual = calculateProgressiveTax(chargeableIncomeAnnual, NIGERIA_TAX_BANDS);

  // Only Pension, NHF (not collected in this form) and PAYE are actual payroll deductions.
  const netPayAnnual = grossAnnual - pensionAnnual - payeAnnual;
  const totalDeductionsAnnual = grossAnnual - netPayAnnual;

  const divisor = period === 'monthly' ? 12 : 1;

  return {
    grossPay: grossAnnual / divisor,
    paye: payeAnnual / divisor,
    netPay: netPayAnnual / divisor,
    totalDeductions: totalDeductionsAnnual / divisor,
  };
}

const TaxCalculator = () => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(null);
  const [period, setPeriod] = useState<Period>('monthly');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);

  // Shared / Ghana income fields
  const [grossIncome, setGrossIncome] = useState(''); // Ghana: Basic Salary
  const [cashAllowance, setCashAllowance] = useState('');
  const [ghanaResidency, setGhanaResidency] = useState<Residency>('resident');

  // Nigeria-specific fields
  const [lifeInsurance, setLifeInsurance] = useState('');
  const [nhis, setNhis] = useState('');
  const [rent, setRent] = useState('');
  const [loanInterest, setLoanInterest] = useState('');
  const [pensionContribution, setPensionContribution] = useState('');

  const selectCountry = (country: Country) => {
    setSelectedCountry(country);
    setPeriod('monthly');
    setCountryDropdownOpen(false);

    setGrossIncome('');
    setCashAllowance('');
    setGhanaResidency('resident');
    setLifeInsurance('');
    setNhis('');
    setRent('');
    setLoanInterest('');
    setPensionContribution('');
  };

  const closeCalculator = () => {
    setSelectedCountry(null);
    setGrossIncome('');
    setCashAllowance('');
    setGhanaResidency('resident');
    setLifeInsurance('');
    setNhis('');
    setRent('');
    setLoanInterest('');
    setPensionContribution('');
    setPeriod('monthly');
    setCountryDropdownOpen(false);
  };

  const formatCurrency = (value: number, currency: 'NGN' | 'GHS') => {
    const numericValue = Number.isFinite(value) ? value : 0;

    if (currency === 'NGN') {
      return `₦${numericValue.toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    return `GH₵${numericValue.toLocaleString('en-GH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const periodLabel = period === 'monthly' ? 'Monthly' : 'Annual';

  const ghanaResult = useMemo(
    () =>
      calculateGhanaTax({
        basicSalary: Number(grossIncome || 0),
        cashAllowance: Number(cashAllowance || 0),
        period,
        residency: ghanaResidency,
      }),
    [grossIncome, cashAllowance, period, ghanaResidency]
  );

  const nigeriaResult = useMemo(
    () =>
      calculateNigeriaTax({
        grossIncome: Number(grossIncome || 0),
        lifeInsurance: Number(lifeInsurance || 0),
        nhis: Number(nhis || 0),
        rent: Number(rent || 0),
        loanInterest: Number(loanInterest || 0),
        pensionContribution: Number(pensionContribution || 0),
        period,
      }),
    [grossIncome, lifeInsurance, nhis, rent, loanInterest, pensionContribution, period]
  );

  return (
    <section className="tax-section" id="pricing">
      <div className="tax-container">

        {/* =====================================================
            DEFAULT COUNTRY SELECTION
            ===================================================== */}
        {!selectedCountry && (
          <div className="country-selection">
            <div className="tax-badge">TAX CALCULATOR</div>

            <div className="country-header">
              <h2>
                Calculate your <span>PAYE tax</span> instantly
              </h2>

              <p>
                Accurate payroll tax calculations for Nigeria and Ghana.
                Select your country, enter your income details, and get
                results in real time.
              </p>
            </div>

            <div className="calculator-features default-features">
              <div>
                <Check size={13} />
                <span>FIRS Compliant — Nigeria</span>
              </div>

              <div>
                <Check size={13} />
                <span>GRA Compliant — Ghana</span>
              </div>

              <div>
                <Check size={13} />
                <span>Real-time Calculation</span>
              </div>
            </div>

            <div className="country-selector-wrapper default-selector">
              <span>SELECT COUNTRY</span>

              <button
                type="button"
                className="country-selector"
                onClick={() =>
                  setCountryDropdownOpen(!countryDropdownOpen)
                }
              >
                <span className="selector-placeholder">
                  Select country
                </span>

                <ChevronDown
                  size={16}
                  className={
                    countryDropdownOpen
                      ? 'selector-chevron open'
                      : 'selector-chevron'
                  }
                />
              </button>

              {countryDropdownOpen && (
                <div className="country-dropdown">
                  <button
                    type="button"
                    onClick={() => selectCountry('nigeria')}
                  >
                    <Image
                      src="/icons/nigeria.svg"
                      alt="Nigeria"
                      width={22}
                      height={15}
                    />

                    <span>Nigeria</span>

                    <small>FIRS</small>
                  </button>

                  <button
                    type="button"
                    onClick={() => selectCountry('ghana')}
                  >
                    <Image
                      src="/icons/ghana.svg"
                      alt="Ghana"
                      width={22}
                      height={15}
                    />

                    <span>Ghana</span>

                    <small>GRA</small>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* =====================================================
            GHANA CALCULATOR
            ===================================================== */}
        {selectedCountry === 'ghana' && (
          <div className="calculator-view">

            {/* HERO */}
            <div className="calculator-hero">
              <div className="calculator-badge">
                TAX CALCULATOR
              </div>

              <div className="calculator-hero-content">
                <h2>
                  Calculate your <span>PAYE tax</span> instantly
                </h2>

                <p>
                  Accurate payroll tax calculations for Ghana.
                  Enter your income details and get results in real time.
                </p>

                <div className="calculator-features">
                  <div>
                    <Check size={13} />
                    <span>FIRS Compliant — Nigeria</span>
                  </div>

                  <div>
                    <Check size={13} />
                    <span>GRA Compliant — Ghana</span>
                  </div>

                  <div>
                    <Check size={13} />
                    <span>Real-time Calculation</span>
                  </div>
                </div>

                {/* COUNTRY SELECTOR */}
                <div className="country-selector-wrapper">
                  <span>SELECT COUNTRY</span>

                  <button
                    type="button"
                    className="country-selector"
                    onClick={() =>
                      setCountryDropdownOpen(!countryDropdownOpen)
                    }
                  >
                    <Image
                      src="/icons/ghana.svg"
                      alt="Ghana"
                      width={21}
                      height={14}
                    />

                    <strong>Ghana</strong>

                    <span className="authority-badge">
                      GRA
                    </span>

                    <ChevronDown
                      size={15}
                      className={
                        countryDropdownOpen
                          ? 'selector-chevron open'
                          : 'selector-chevron'
                      }
                    />
                  </button>

                  {countryDropdownOpen && (
                    <div className="country-dropdown">
                      <button
                        type="button"
                        onClick={() => selectCountry('nigeria')}
                      >
                        <Image
                          src="/icons/nigeria.svg"
                          alt="Nigeria"
                          width={22}
                          height={15}
                        />

                        <span>Nigeria</span>

                        <small>FIRS</small>
                      </button>

                      <button
                        type="button"
                        onClick={() => selectCountry('ghana')}
                      >
                        <Image
                          src="/icons/ghana.svg"
                          alt="Ghana"
                          width={22}
                          height={15}
                        />

                        <span>Ghana</span>

                        <small>GRA</small>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* BODY */}
            <div className="calculator-body">

              {/* FORM */}
              <div className="calculator-form">

                {/* HEADER */}
                <div className="form-header">
                  <div>
                    <h3>
                      <Image
                        src="/icons/ghana.svg"
                        alt="Ghana"
                        width={22}
                        height={15}
                      />
                      Ghana
                    </h3>

                    <p>
                      PAYE via Ghana Revenue Authority
                    </p>
                  </div>

                  <div className="period-toggle">
                    <button
                      type="button"
                      className={
                        period === 'monthly'
                          ? 'active'
                          : ''
                      }
                      onClick={() => setPeriod('monthly')}
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      className={
                        period === 'annual'
                          ? 'active'
                          : ''
                      }
                      onClick={() => setPeriod('annual')}
                    >
                      Annual
                    </button>
                  </div>
                </div>

                {/* TAX RESIDENCE */}
                <div className="form-section">
                  <p className="form-section-title">
                    TAX RESIDENCE
                  </p>

                  <div className="residence-options">
                    <button
                      type="button"
                      className={
                        ghanaResidency === 'resident'
                          ? 'residence-option active'
                          : 'residence-option'
                      }
                      onClick={() => setGhanaResidency('resident')}
                    >
                      <strong>Resident</strong>
                      <span>
                        Progressive 0%–35%
                      </span>
                    </button>

                    <button
                      type="button"
                      className={
                        ghanaResidency === 'nonresident'
                          ? 'residence-option active'
                          : 'residence-option'
                      }
                      onClick={() => setGhanaResidency('nonresident')}
                    >
                      <strong>Non-Resident</strong>
                      <span>
                        Flat 25% rate
                      </span>
                    </button>
                  </div>
                </div>

                {/* INCOME */}
                <div className="form-section">
                  <p className="form-section-title">
                    INCOME
                  </p>

                  <div className="input-grid">

                    {/* BASIC SALARY */}
                    <div className="input-group">
                      <label>
                        Basic Salary

                        <span>
                          <Info size={12} />
                          {periodLabel}
                        </span>
                      </label>

                      <div className="money-input">
                        <span>GH₵</span>

                        <input
                          type="number"
                          placeholder="0.00"
                          value={grossIncome}
                          onChange={(e) =>
                            setGrossIncome(e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* CASH ALLOWANCE */}
                    <div className="input-group">
                      <label>
                        Taxable Cash Allowances

                        <span>
                          <Info size={12} />
                          {periodLabel}
                        </span>
                      </label>

                      <div className="money-input">
                        <span>GH₵</span>

                        <input
                          type="number"
                          placeholder="0.00"
                          value={cashAllowance}
                          onChange={(e) =>
                            setCashAllowance(e.target.value)
                          }
                        />
                      </div>
                    </div>

                  </div>
                </div>

                {/* SSNIT */}
                <div className="form-section">
                  <p className="form-section-title">
                    SSNIT CONTRIBUTION
                  </p>

                  <div className="contribution-box">
                    <div className="contribution-check">
                      <Check
                        size={15}
                        strokeWidth={3}
                      />
                    </div>

                    <div>
                      <strong>
                        {ghanaResidency === 'resident'
                          ? 'Employee contributes to SSNIT'
                          : 'SSNIT — pending consultant confirmation'}
                      </strong>

                      <p>
                        {ghanaResidency === 'resident'
                          ? `5.5% of basic salary — deducted before tax (capped at GH₵${GHANA_SSNIT_MONTHLY_CEILING.toLocaleString()}/month insurable earnings)`
                          : 'Non-residents are generally exempt from SSNIT; confirm with a tax consultant.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* RESULTS */}
              <div className="calculator-results">
                <div className="results-panel">

                  <div className="results-title">
                    <BarChart3 size={15} />
                    RESULTS — {period.toUpperCase()}
                  </div>

                  {/* NET PAY */}
                  <div className="result-card primary">
                    <div className="result-icon">
                      <WalletCards size={20} />
                    </div>

                    <div>
                      <span>Net Pay</span>

                      <strong>
                        {formatCurrency(ghanaResult.netPay, 'GHS')}
                      </strong>

                      <small>
                        Gross − SSNIT − PAYE
                      </small>
                    </div>
                  </div>

                  {/* SSNIT */}
                  <div className="result-card">
                    <div className="result-icon light">
                      <ReceiptText size={19} />
                    </div>

                    <div>
                      <span>
                        Employee SSNIT
                      </span>

                      <strong>
                        {formatCurrency(ghanaResult.ssnit, 'GHS')}
                      </strong>

                      <small>
                        5.5% of basic salary
                      </small>
                    </div>
                  </div>

                  {/* PAYE */}
                  <div className="result-card">
                    <div className="result-icon light">
                      <TrendingDown size={19} />
                    </div>

                    <div>
                      <span>
                        Total PAYE
                      </span>

                      <strong>
                        {formatCurrency(ghanaResult.paye, 'GHS')}
                      </strong>

                      <small>
                        {ghanaResidency === 'resident'
                          ? 'Progressive GRA rates'
                          : 'Flat 25% (non-resident)'}
                      </small>
                    </div>
                  </div>
                </div>

                {/* CALCULATION METHOD */}
                <div className="calculation-method">
                  <div className="calculation-title">
                    <Info size={14} />
                    Calculation method
                  </div>

                  <p>
                    {ghanaResidency === 'resident'
                      ? 'GRA 2025/26 resident bands (0% → 35%) applied on chargeable income after SSNIT deduction.'
                      : 'Flat 25% applied to gross Ghana-sourced income — no band calculation, no SSNIT deducted.'}
                  </p>
                </div>
              </div>
            </div>

            {/* CLOSE */}
            <button
              type="button"
              className="close-calculator"
              onClick={closeCalculator}
            >
              <X size={15} />
              Close calculator
            </button>
          </div>
        )}

        {/* =====================================================
            NIGERIA CALCULATOR
            ===================================================== */}
        {selectedCountry === 'nigeria' && (
          <div className="calculator-view nigeria-view">

            {/* HERO */}
            <div className="calculator-hero">
              <div className="calculator-badge">
                TAX CALCULATOR
              </div>

              <div className="calculator-hero-content">
                <h2>
                  Calculate your <span>PAYE tax</span> instantly
                </h2>

                <p>
                  Accurate payroll tax calculations for Nigeria.
                  Enter your income details and get results in real time.
                </p>

                <div className="calculator-features">
                  <div>
                    <Check size={13} />
                    <span>FIRS Compliant — Nigeria</span>
                  </div>

                  <div>
                    <Check size={13} />
                    <span>GRA Compliant — Ghana</span>
                  </div>

                  <div>
                    <Check size={13} />
                    <span>Real-time Calculation</span>
                  </div>
                </div>

                {/* COUNTRY SELECTOR */}
                <div className="country-selector-wrapper">
                  <span>SELECT COUNTRY</span>

                  <button
                    type="button"
                    className="country-selector"
                    onClick={() =>
                      setCountryDropdownOpen(!countryDropdownOpen)
                    }
                  >
                    <Image
                      src="/icons/nigeria.svg"
                      alt="Nigeria"
                      width={21}
                      height={14}
                    />

                    <strong>Nigeria</strong>

                    <span className="authority-badge">
                      FIRS
                    </span>

                    <ChevronDown
                      size={15}
                      className={
                        countryDropdownOpen
                          ? 'selector-chevron open'
                          : 'selector-chevron'
                      }
                    />
                  </button>

                  {countryDropdownOpen && (
                    <div className="country-dropdown">
                      <button
                        type="button"
                        onClick={() =>
                          selectCountry('nigeria')
                        }
                      >
                        <Image
                          src="/icons/nigeria.svg"
                          alt="Nigeria"
                          width={22}
                          height={15}
                        />

                        <span>Nigeria</span>

                        <small>FIRS</small>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          selectCountry('ghana')
                        }
                      >
                        <Image
                          src="/icons/ghana.svg"
                          alt="Ghana"
                          width={22}
                          height={15}
                        />

                        <span>Ghana</span>

                        <small>GRA</small>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* BODY */}
            <div className="calculator-body">

              {/* FORM */}
              <div className="calculator-form">

                {/* HEADER */}
                <div className="form-header">
                  <div>
                    <h3>
                      <Image
                        src="/icons/nigeria.svg"
                        alt="Nigeria"
                        width={22}
                        height={15}
                      />
                      Nigeria
                    </h3>

                    <p>
                      PAYE via Federal Inland Revenue Service
                    </p>
                  </div>

                  <div className="period-toggle">
                    <button
                      type="button"
                      className={
                        period === 'monthly'
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setPeriod('monthly')
                      }
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      className={
                        period === 'annual'
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setPeriod('annual')
                      }
                    >
                      Annual
                    </button>
                  </div>
                </div>

                {/* INCOME */}
                <div className="form-section">
                  <p className="form-section-title">
                    INCOME
                  </p>

                  <div className="input-group full">
                    <label>
                      Total {periodLabel} Gross Income

                      <span>
                        <Info size={12} />
                        {periodLabel} figure
                      </span>
                    </label>

                    <div className="money-input large">
                      <span>₦</span>

                      <input
                        type="number"
                        placeholder="0"
                        value={grossIncome}
                        onChange={(e) =>
                          setGrossIncome(e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* ALLOWABLE DEDUCTIONS */}
                <div className="form-section">
                  <p className="form-section-title">
                    ALLOWABLE DEDUCTIONS
                  </p>

                  <div className="input-grid">

                    {/* LIFE INSURANCE */}
                    <div className="input-group">
                      <label>
                        Life Insurance Premium
                        (You & Spouse)

                        <span>
                          <Info size={12} />
                          {periodLabel}
                        </span>
                      </label>

                      <div className="money-input">
                        <span>₦</span>

                        <input
                          type="number"
                          placeholder="0"
                          value={lifeInsurance}
                          onChange={(e) =>
                            setLifeInsurance(e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* NHIS */}
                    <div className="input-group">
                      <label>
                        NHIS Contribution

                        <span>
                          <Info size={12} />
                          {periodLabel}
                        </span>
                      </label>

                      <div className="money-input">
                        <span>₦</span>

                        <input
                          type="number"
                          placeholder="0"
                          value={nhis}
                          onChange={(e) =>
                            setNhis(e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* RENT */}
                    <div className="input-group">
                      <label>
                        Annual Rent

                        <span>
                          <Info size={12} />
                          Annual
                        </span>
                      </label>

                      <div className="money-input">
                        <span>₦</span>

                        <input
                          type="number"
                          placeholder="0"
                          value={rent}
                          onChange={(e) =>
                            setRent(e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* LOAN */}
                    <div className="input-group">
                      <label>
                        Interest on Loan —
                        Owner Occupied House

                        <span>
                          <Info size={12} />
                          Annual
                        </span>
                      </label>

                      <div className="money-input">
                        <span>₦</span>

                        <input
                          type="number"
                          placeholder="0.00"
                          value={loanInterest}
                          onChange={(e) =>
                            setLoanInterest(e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* PENSION */}
                    <div className="input-group">
                      <label>
                        {periodLabel} Employee Pension
                        Contribution

                        <span>
                          <Info size={12} />
                          {periodLabel} (min 8%)
                        </span>
                      </label>

                      <div className="money-input">
                        <span>₦</span>

                        <input
                          type="number"
                          placeholder="0.00"
                          value={pensionContribution}
                          onChange={(e) =>
                            setPensionContribution(e.target.value)
                          }
                        />
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* RESULTS */}
              <div className="calculator-results">
                <div className="results-panel">

                  <div className="results-title">
                    <BarChart3 size={15} />
                    RESULTS — {period.toUpperCase()}
                  </div>

                  {/* NET PAY */}
                  <div className="result-card primary">
                    <div className="result-icon">
                      <WalletCards size={20} />
                    </div>

                    <div>
                      <span>Net Pay</span>

                      <strong>
                        {formatCurrency(nigeriaResult.netPay, 'NGN')}
                      </strong>

                      <small>
                        After pension & PAYE
                      </small>
                    </div>
                  </div>

                  {/* PAYE */}
                  <div className="result-card">
                    <div className="result-icon light">
                      <ReceiptText size={19} />
                    </div>

                    <div>
                      <span>
                        PAYE Tax Due
                      </span>

                      <strong>
                        {formatCurrency(nigeriaResult.paye, 'NGN')}
                      </strong>

                      <small>
                        Income tax payable
                      </small>
                    </div>
                  </div>

                  {/* DEDUCTIONS */}
                  <div className="result-card">
                    <div className="result-icon light">
                      <TrendingDown size={19} />
                    </div>

                    <div>
                      <span>
                        Total Deductions
                      </span>

                      <strong>
                        {formatCurrency(nigeriaResult.totalDeductions, 'NGN')}
                      </strong>

                      <small>
                        Pension + PAYE
                      </small>
                    </div>
                  </div>
                </div>

                {/* CALCULATION METHOD */}
                <div className="calculation-method">
                  <div className="calculation-title">
                    <Info size={14} />
                    Calculation method
                  </div>

                  <p>
                    Chargeable Income = Gross − Pension − NHIS −
                    Life Insurance − (Rent Relief OR Loan Interest
                    Relief). NTA 2025/2026 progressive bands
                    (0% → 25%) applied to arrive at PAYE.
                  </p>
                </div>
              </div>
            </div>

            {/* CLOSE */}
            <button
              type="button"
              className="close-calculator"
              onClick={closeCalculator}
            >
              <X size={15} />
              Close calculator
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TaxCalculator;